import { formatToCurrency } from "src/common/parser";
import { Languages } from "../../types";
import { PatientSearchDescriptions } from "./types";
import dayjs from 'dayjs'
import { SystemConstants } from "src/common/constants";
import { PaymentPlan } from "src/common/models/paymentPlan";

const DescriptionsEnUs: PatientSearchDescriptions = {
    TITLE: "Patient Search",
    SEARCH_FORM: {
        SEARCH_VALUE: {
            LABEL: "Search Value",
            PLACEHOLDER: "Enter search value",
        },
        SEARCH_TYPE: {
            NAME: "Name",
            SURNAME: "Surname",
            IDENTIFICATION: "Identification",
            PHONE: "Phone",
            ID: "ID",
            EMAIL: "Email",
        },
        SEARCH_BUTTON: "Search",
    },
    SEARCH_RESULTS: {
        EMPTY_STATE: {
            TITLE: "No Patients Found",
            DESCRIPTION: "Try adjusting your search filters or add a new patient record to get started.",
        },
        INFO_BAR: {
            INFO_TITLE: (searchType: string, searchValue: string) => `Search Results for type ${searchType}: "${searchValue}".`,
            TOTAL_COMPLETE_RESULTS: (display: number, total: number) => `Showing first ${display} of ${total} coincidences.`,
            TOTAL_PARTIAL_RESULTS: (display: number) => `Showing ${display} results.`,
        },
        TOOLTIP: {
            EMAIL: "Email",
            PHONE: "Phone",
            IDENTIFICATION: "Identification",
            ID: "Patient Id",
        },
    },
    PERSONAL_DATA: {
        TITLE: "Personal Data",
        CUSTOMER: {
            LABEL: "Patient",
            PLACEHOLDER: "Select patient",
        },
        EMAIL: {
            LABEL: "Email",
            PLACEHOLDER: "Email",
        },
        PHONE: {
            LABEL: "Phone",
            PLACEHOLDER: "Phone",
        },
        BALANCE: {
            LABEL: "Balance",
            PLACEHOLDER: "Balance",
        },
        GETTING_PATIENT_DATA: "Getting patient data.",
        ERROR_GETTING_PATIENTS: "An error ocurred while getting patients.",
        PLANS_VISIBILITY: (numberOfPlans: number, hide?: boolean) => {
            if (numberOfPlans === 0) {
                return `No Plans`
            }
            return hide ? `Show ${numberOfPlans} Plan(s)` : `Hide ${numberOfPlans} Plan(s)`
        },
        PLAN_DATA_COMPOSSER: (paymentPlan: PaymentPlan) => {
            const datetime: string = dayjs(paymentPlan.datetime).format(SystemConstants.DATETIME_FORMAT_DISPLAY)
            const amount: string = formatToCurrency(paymentPlan.amount.toString())
            const amountPayed: string = formatToCurrency(paymentPlan.amountPayed.toString())
            const outstanding: string = formatToCurrency(
                (parseFloat(paymentPlan.amount.toString()) - parseFloat(paymentPlan.amountPayed.toString())).toString()
            )

            return `Created at: ${datetime}. Total: ${amount}. Payed: ${amountPayed}. Outstanding: ${outstanding}`
        },
    },
}

const DescriptionsEsUs: PatientSearchDescriptions = {
    TITLE: "Buscar Paciente",
    SEARCH_FORM: {
        SEARCH_VALUE: {
            LABEL: "Búsqueda",
            PLACEHOLDER: "Ingrésa término de búsqueda",
        },
        SEARCH_TYPE: {
            NAME: "Nombres",
            SURNAME: "Apellidos",
            IDENTIFICATION: "Identificación",
            PHONE: "Teléfono",
            ID: "Código",
            EMAIL: "Correo",
        },
        SEARCH_BUTTON: "Buscar",
    },
    SEARCH_RESULTS: {
        EMPTY_STATE: {
            TITLE: "Sin Resultados",
            DESCRIPTION: "Intenta nuevamente ajustando los filtros o el término de búsqueda.",
        },
        INFO_BAR: {
            INFO_TITLE: (searchType: string, searchValue: string) => `Resultados de búsqueda para ${searchType}: "${searchValue}".`,
            TOTAL_COMPLETE_RESULTS: (display: number, total: number) => `Mostrando primeros ${display} de ${total} coincidencia(s).`,
            TOTAL_PARTIAL_RESULTS: (display: number) => `Mostrando ${display} coincidencia(s).`,
        },
        TOOLTIP: {
            EMAIL: "Correo",
            PHONE: "Teléfono",
            IDENTIFICATION: "DPI",
            ID: "Código",
        },
    },
    PERSONAL_DATA: {
        TITLE: "Datos personales",
        CUSTOMER: {
            LABEL: "Paciente",
            PLACEHOLDER: "Seleccione paciente",
        },
        EMAIL: {
            LABEL: "Correo",
            PLACEHOLDER: "Correo",
        },
        PHONE: {
            LABEL: "Teléfono",
            PLACEHOLDER: "Teléfono",
        },
        BALANCE: {
            LABEL: "Saldo",
            PLACEHOLDER: "Saldo",
        },
        GETTING_PATIENT_DATA: "Obeniendo información del paciente.",
        ERROR_GETTING_PATIENTS: "Ha ocurrido un error al obtener los pacientes.",
        PLANS_VISIBILITY: (numberOfPlans: number, hide?: boolean) => {
            if (numberOfPlans === 0) {
                return `Sin planes`
            }
            return hide ? `Mostrar ${numberOfPlans} Plan(es)` : `Ocultar ${numberOfPlans} Plan(es)`
        },
        PLAN_DATA_COMPOSSER: (paymentPlan: PaymentPlan) => {
            const datetime: string = dayjs(paymentPlan.datetime).format(SystemConstants.DATETIME_FORMAT_DISPLAY)
            const amount: string = formatToCurrency(paymentPlan.amount.toString())
            const amountPayed: string = formatToCurrency(paymentPlan.amountPayed.toString())
            const outstanding: string = formatToCurrency(
                (parseFloat(paymentPlan.amount.toString()) - parseFloat(paymentPlan.amountPayed.toString())).toString()
            )

            return `Creado: ${datetime}. Total: ${amount}. Pagado: ${amountPayed}. Pendiente: ${outstanding}`
        },
    },
}

const getPatientSearchDictionary = {}
getPatientSearchDictionary[Languages.ENGLISH_US] = DescriptionsEnUs
getPatientSearchDictionary[Languages.SPANISH_US] = DescriptionsEsUs

export const getPatientSearchDescriptions = (systemLang: Languages): PatientSearchDescriptions => (
    getPatientSearchDictionary[systemLang]
)